/**
 * Tournament Service - API calls for tournament feature
 *
 * Multi-tournament support:
 * - List/filter tournaments by status
 * - Create user tournaments
 * - Registration with capacity check
 * - Match operations (Edge Functions: generate-bracket, start-round,
 *   process-match, submit-answer, tick-tournament)
 * - Bracket data via select direto em tournament_matches
 */

import { supabase } from '../lib/supabase';
import { withTimeout } from '../utils/community/async';

// Rede de segurança: o fetch do RN não tem timeout nativo — requisição que
// estola sem resposta deixava tela em spinner infinito (ex.: "Criando..." na
// criação de torneio). Toda chamada deste service ASSENTA em até 12s:
// resolve, ou rejeita com mensagem legível que as telas mostram no Alert.
const TIMEOUT_MS = 12000;
const tq = (builder, label) => withTimeout(
    builder,
    TIMEOUT_MS,
    `Sem resposta do servidor (${label}). Verifique sua conexão e tente novamente.`
);

/**
 * Invoke a tournament Edge Function.
 * Erro chega por DOIS canais: `error` (rede/HTTP) e `data.success === false`
 * (erro de negócio com HTTP 400) — normaliza ambos em throw.
 */
const invokeFn = async (name, body) => {
    const { data, error } = await tq(supabase.functions.invoke(name, { body }), name);
    if (error) throw error;
    if (data && data.success === false) throw new Error(data.error || `${name} failed`);
    return data;
};

// =====================================================
// TOURNAMENT QUERIES
// =====================================================

/**
 * Get tournaments by status filter
 * Uses tournament_summary view for participant count
 * (listas públicas: torneios privados só via invite code / getMyTournaments)
 */
export const getTournamentsByStatus = async (status, limit = 20) => {
    const { data, error } = await tq(supabase
        .from('tournament_summary')
        .select('*')
        .eq('status', status)
        .eq('visibility', 'public')
        .order('start_time', { ascending: status === 'registration' })
        .limit(limit), 'lista de torneios');

    if (error) throw error;
    return data || [];
};

/**
 * Get multiple status tournaments (e.g. registration + in_progress)
 */
export const getActiveTournaments = async () => {
    const { data, error } = await tq(supabase
        .from('tournament_summary')
        .select('*')
        .in('status', ['registration', 'in_progress'])
        .eq('visibility', 'public')
        .order('start_time', { ascending: true }), 'torneios ativos');

    if (error) throw error;
    return data || [];
};

/**
 * Get tournaments created by or registered by a user
 */
export const getMyTournaments = async (userId) => {
    // Get tournaments where user is creator
    const { data: created, error: createdError } = await tq(supabase
        .from('tournament_summary')
        .select('*')
        .eq('created_by', userId)
        .in('status', ['upcoming', 'registration', 'in_progress'])
        .order('created_at', { ascending: false }), 'meus torneios');

    if (createdError) throw createdError;

    // Get tournaments where user is registered
    const { data: registrations, error: regError } = await tq(supabase
        .from('tournament_registrations')
        .select('tournament_id')
        .eq('user_id', userId), 'minhas inscrições');

    if (regError) throw regError;

    const registeredIds = (registrations || []).map(r => r.tournament_id);

    let registered = [];
    if (registeredIds.length > 0) {
        const { data, error } = await tq(supabase
            .from('tournament_summary')
            .select('*')
            .in('id', registeredIds)
            .in('status', ['upcoming', 'registration', 'in_progress'])
            .order('start_time', { ascending: true }), 'torneios inscritos');

        if (error) throw error;
        registered = data || [];
    }

    // Merge and deduplicate
    const allTournaments = [...created];
    registered.forEach(t => {
        if (!allTournaments.find(c => c.id === t.id)) {
            allTournaments.push(t);
        }
    });

    return allTournaments;
};

/**
 * Get completed tournaments (history)
 */
export const getTournamentHistory = async (limit = 20) => {
    const { data, error } = await tq(supabase
        .from('tournament_summary')
        .select(`
            *,
            champion:profiles!tournaments_champion_id_fkey(id, nome, foto_url),
            runner_up:profiles!tournaments_runner_up_id_fkey(id, nome, foto_url)
        `)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(limit), 'histórico de campeões');

    if (error) throw error;
    return data || [];
};

/**
 * Get tournament by ID
 */
export const getTournamentById = async (tournamentId) => {
    const { data, error } = await tq(supabase
        .from('tournament_summary')
        .select('*')
        .eq('id', tournamentId)
        .single(), 'dados do torneio');

    if (error) throw error;
    return data;
};

/**
 * Get tournament by invite code (private tournaments)
 */
export const getTournamentByInviteCode = async (code) => {
    const { data, error } = await tq(supabase
        .from('tournament_summary')
        .select('*')
        .eq('invite_code', code.toUpperCase())
        .single(), 'código de convite');

    if (error && error.code !== 'PGRST116') throw error;
    return data;
};

// =====================================================
// TOURNAMENT CREATION
// =====================================================

/**
 * Create a new tournament (any authenticated user)
 */
export const createTournament = async (tournamentData) => {
    const { data, error } = await tq(supabase
        .from('tournaments')
        .insert(tournamentData)
        .select()
        .single(), 'criar torneio');

    if (error) throw error;

    // Auto-register the creator
    await registerForTournament(data.id, tournamentData.created_by);

    return data;
};

/**
 * Check how many active tournaments a user has created (limit: 3)
 */
export const getUserActiveTournamentCount = async (userId) => {
    const { count, error } = await tq(supabase
        .from('tournaments')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', userId)
        .in('status', ['upcoming', 'registration', 'in_progress']), 'limite de torneios');

    if (error) throw error;
    return count || 0;
};

/**
 * Cancel tournament (only creator, before in_progress)
 */
export const cancelTournament = async (tournamentId, userId) => {
    const { data, error } = await tq(supabase
        .from('tournaments')
        .update({ status: 'cancelled' })
        .eq('id', tournamentId)
        .eq('created_by', userId)
        .in('status', ['upcoming', 'registration'])
        .select()
        .single(), 'cancelar torneio');

    if (error) throw error;
    return data;
};

/**
 * Manually start tournament early (only creator, if min_participants reached)
 * NÃO seta status direto: antecipa o start_time e cutuca o tick — quem
 * transiciona registration→in_progress é o gate atômico do generate-bracket.
 */
export const startTournamentEarly = async (tournamentId, userId) => {
    // Verify ownership and min participants
    const tournament = await getTournamentById(tournamentId);

    if (!tournament) throw new Error('Torneio não encontrado');
    if (tournament.created_by !== userId) throw new Error('Apenas o criador pode iniciar');
    if (tournament.participant_count < tournament.min_participants) {
        throw new Error(`Mínimo de ${tournament.min_participants} participantes necessário`);
    }

    const now = new Date().toISOString();
    const { data, error } = await tq(supabase
        .from('tournaments')
        .update({
            start_time: now,
            registration_end: now,
        })
        .eq('id', tournamentId)
        .select()
        .single(), 'iniciar torneio');

    if (error) throw error;

    await tickTournament(tournamentId);
    return data;
};

// =====================================================
// REGISTRATION
// =====================================================

/**
 * Register user for a tournament
 */
export const registerForTournament = async (tournamentId, userId) => {
    // Check if already registered
    const { data: existing } = await tq(supabase
        .from('tournament_registrations')
        .select('id')
        .eq('tournament_id', tournamentId)
        .eq('user_id', userId)
        .single(), 'checar inscrição');

    if (existing) {
        throw new Error('Você já está inscrito neste torneio');
    }

    // Check capacity
    const { count } = await tq(supabase
        .from('tournament_registrations')
        .select('*', { count: 'exact', head: true })
        .eq('tournament_id', tournamentId), 'vagas do torneio');

    const tournament = await getTournamentById(tournamentId);

    if (count >= tournament.max_participants) {
        throw new Error('Torneio lotado');
    }

    if (tournament.status !== 'registration') {
        throw new Error('Inscrições encerradas');
    }

    // Register
    const { data, error } = await tq(supabase
        .from('tournament_registrations')
        .insert({
            tournament_id: tournamentId,
            user_id: userId,
        })
        .select()
        .single(), 'inscrever no torneio');

    if (error) throw error;
    return data;
};

/**
 * Unregister from tournament (before it starts)
 */
export const unregisterFromTournament = async (tournamentId, userId) => {
    const tournament = await getTournamentById(tournamentId);
    if (tournament.status !== 'registration') {
        throw new Error('Não é possível cancelar após o início');
    }

    const { error } = await tq(supabase
        .from('tournament_registrations')
        .delete()
        .eq('tournament_id', tournamentId)
        .eq('user_id', userId), 'cancelar inscrição');

    if (error) throw error;
    return true;
};

/**
 * Check if user is registered for a tournament
 */
export const checkUserRegistration = async (tournamentId, userId) => {
    const { data, error } = await tq(supabase
        .from('tournament_registrations')
        .select('*')
        .eq('tournament_id', tournamentId)
        .eq('user_id', userId)
        .single(), 'minha inscrição');

    if (error && error.code !== 'PGRST116') throw error;
    return data;
};

/**
 * Get tournament participants
 */
export const getTournamentParticipants = async (tournamentId) => {
    const { data, error } = await tq(supabase
        .from('tournament_registrations')
        .select(`
            *,
            user:profiles(id, nome, foto_url)
        `)
        .eq('tournament_id', tournamentId)
        .order('registered_at', { ascending: true }), 'participantes');

    if (error) throw error;
    return data || [];
};

// =====================================================
// BRACKET & MATCHES
// =====================================================

/**
 * Get tournament bracket — select direto com perfis embedados
 * (exige as FKs player→profiles da migration tournament_async_v1)
 */
export const getTournamentBracket = async (tournamentId) => {
    const { data, error } = await tq(supabase
        .from('tournament_matches')
        .select(`
            *,
            player1:profiles!tournament_matches_player1_id_fkey(id, nome, foto_url),
            player2:profiles!tournament_matches_player2_id_fkey(id, nome, foto_url)
        `)
        .eq('tournament_id', tournamentId)
        .order('round', { ascending: false })
        .order('match_number', { ascending: true }), 'bracket');

    if (error) throw error;
    return data || [];
};

/**
 * Get user's current live match in a tournament
 */
export const getUserCurrentMatch = async (tournamentId, userId) => {
    const { data, error } = await tq(supabase
        .from('tournament_matches')
        .select('*')
        .eq('tournament_id', tournamentId)
        .eq('status', 'live')
        .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
        .single(), 'minha partida');

    if (error && error.code !== 'PGRST116') throw error;
    return data;
};

/**
 * Get match details
 */
export const getMatchDetails = async (matchId) => {
    const { data, error } = await tq(supabase
        .from('tournament_matches')
        .select(`
            *,
            player1:profiles!tournament_matches_player1_id_fkey(id, nome, foto_url),
            player2:profiles!tournament_matches_player2_id_fkey(id, nome, foto_url)
        `)
        .eq('id', matchId)
        .single(), 'dados da partida');

    if (error) throw error;
    return data;
};

// =====================================================
// ANSWERS
// =====================================================

/**
 * Submit an answer for a tournament match (Edge Function submit-answer).
 * is_correct é computado no SERVER; resposta traz
 * { is_correct, my_progress, opponent_progress, match_complete }.
 * Auto-processa o match quando ambos terminam.
 */
export const submitMatchAnswer = async (matchId, userId, questionIndex, selectedOption, timeMs) => {
    return invokeFn('submit-answer', {
        match_id: matchId,
        user_id: userId,
        question_index: questionIndex,
        selected_option: selectedOption,
        time_ms: timeMs,
    });
};

/**
 * Get user's answers for a match
 */
export const getUserMatchAnswers = async (matchId, userId) => {
    const { data, error } = await tq(supabase
        .from('tournament_answers')
        .select('*')
        .eq('match_id', matchId)
        .eq('user_id', userId)
        .order('question_index', { ascending: true }), 'minhas respostas');

    if (error) throw error;
    return data || [];
};

// =====================================================
// EDGE FUNCTIONS (motor do torneio)
// =====================================================

/**
 * Generate bracket for a tournament (Edge Function)
 */
export const generateBracket = async (tournamentId) => {
    return invokeFn('generate-bracket', { tournament_id: tournamentId });
};

/**
 * Start a round (Edge Function)
 */
export const startRound = async (tournamentId, round) => {
    return invokeFn('start-round', { tournament_id: tournamentId, round });
};

/**
 * Process match completion (Edge Function)
 */
export const processMatch = async (matchId) => {
    return invokeFn('process-match', { match_id: matchId });
};

/**
 * Tick do torneio assíncrono (Edge Function tick-tournament): cutuca a
 * máquina de estados — abre bracket no start_time, apura quando a janela
 * fecha, ativa a próxima rodada. Toda validação é server-side.
 */
export const tickTournament = async (tournamentId) => {
    return invokeFn('tick-tournament', { tournament_id: tournamentId });
};

// Throttle local: telas chamam à vontade; no máximo 1 tick por torneio a
// cada 30s por client. Erro de tick nunca quebra a tela (é só um cutucão).
const lastTickAt = new Map();
export const maybeTickTournament = async (tournamentId) => {
    if (!tournamentId) return null;
    const last = lastTickAt.get(tournamentId) || 0;
    if (Date.now() - last < 30000) return null;
    lastTickAt.set(tournamentId, Date.now());
    try {
        return await tickTournament(tournamentId);
    } catch (e) {
        console.warn('[tournament] tick falhou:', e.message);
        return null;
    }
};

export default {
    // Queries
    getTournamentsByStatus,
    getActiveTournaments,
    getMyTournaments,
    getTournamentHistory,
    getTournamentById,
    getTournamentByInviteCode,
    // Creation
    createTournament,
    getUserActiveTournamentCount,
    cancelTournament,
    startTournamentEarly,
    // Registration
    registerForTournament,
    unregisterFromTournament,
    checkUserRegistration,
    getTournamentParticipants,
    // Bracket & Matches
    getTournamentBracket,
    getUserCurrentMatch,
    getMatchDetails,
    // Answers
    submitMatchAnswer,
    getUserMatchAnswers,
    // Edge Functions
    generateBracket,
    startRound,
    processMatch,
    tickTournament,
    maybeTickTournament,
};
